import { useMemo } from 'react';
import { CalendarEvent as SchemaCalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Operatory } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/operatory.pb';
import { Provider } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import type {
  CalendarColumnGroup,
  CalendarColumnHeaderType,
  OperatoryCalendarColumn,
  ProviderCalendarColumn,
} from '@frontend/calendar-grid';
import { IconName } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { ScheduleRequestEvent } from '@frontend/schedule-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { usePreference } from '@frontend/user-preferences';
import { GridCalendarEvent } from '../../components/calendar-view/types';
import { useCalendarLocationIds } from '../../stores/calendar-location.store';
import { useCalendarStore } from '../../stores/calendar.store';
import { DEFAULT_OPERATORY_ICON } from '../../views/ManageOperatoryV3/helpers';
import {
  buildAppointmentGridEvent,
  buildColumnGroup,
  buildScheduleRequestGridEvent,
  buildWorkingHoursAndBreaksEvents,
  getGridEventTiming,
} from './calendar-grid-v3-event-builders';
import { CalendarEntitySchedule } from './calendar-schedule-definitions';

export type CalendarGridV3Props = {
  calendarEventsWithConvertedTimes: SchemaCalendarEvent[];
  scheduledRequestsWithConvertedTimes: ScheduleRequestEvent[];
  sortedProviders: (Provider & { fullName: string })[];
  sortedOperatories: Operatory[];
  providerSchedules: CalendarEntitySchedule[];
};

type CalendarGridV3DataByType<THeader extends CalendarColumnHeaderType> = {
  headerType: THeader;
  columns: THeader extends 'provider' ? ProviderCalendarColumn[] : OperatoryCalendarColumn[];
  events: GridCalendarEvent[];
  columnGroups: CalendarColumnGroup[];
  calendarDate: string;
};

type CalendarGridV3Data = CalendarGridV3DataByType<'provider'> | CalendarGridV3DataByType<'operatory'>;

export const useCalendarGridV3Data = ({
  calendarEventsWithConvertedTimes,
  scheduledRequestsWithConvertedTimes,
  sortedProviders,
  sortedOperatories,
  providerSchedules,
}: CalendarGridV3Props): CalendarGridV3Data => {
  const { calendarEntityType, calendarDate } = useCalendarStore(['calendarEntityType', 'calendarDate']);
  const { calendarLocationIds } = useCalendarLocationIds();
  const { getScopeName } = useAppScopeStore();
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const [columnEntityOrder] = usePreference('ScheduleCalendarViewColumnOrder');

  const isOperatoryView = calendarEntityType === 'operatory';

  return useMemo<CalendarGridV3Data>(() => {
    const events: GridCalendarEvent[] = [];
    const columnGroups: CalendarColumnGroup[] = [];
    let groupOffset = 0;

    // Shared args forwarded to buildColumnGroup for both branches
    const columnGroupBaseArgs = { calendarLocationIds, getScopeName, locationTimezoneMap, columnEntityOrder };

    if (isOperatoryView) {
      const columns: OperatoryCalendarColumn[] = [];

      for (const locationId of calendarLocationIds) {
        const columnIds: string[] = [];
        const locationOperatories = sortedOperatories.filter((o) => o.locationId === locationId);

        locationOperatories.forEach((operatory) => {
          const operatoryId = operatory.id ?? '';
          if (!operatoryId) return;

          const operatoryName = operatory.displayName || operatory.externalName || '';
          const eventIds: string[] = [];
          let appointmentsCount = 0;

          // Operatory appointments
          // ref: getMappedCalendarEventData in use-get-calendar-events-v3.ts
          const operatoryEvents = calendarEventsWithConvertedTimes.filter(
            (e) => e.locationId === locationId && e.operatoryId === operatoryId
          );
          for (const event of operatoryEvents) {
            if (!event.id || !event.startTime) continue;
            const timing = getGridEventTiming(
              calendarDate,
              event.startDate,
              event.startTime,
              event.endDate,
              event.endTime
            );
            events.push(
              buildAppointmentGridEvent(event, timing, getScopeName, {
                entityId: operatoryId,
                entityName: operatoryName,
              })
            );
            eventIds.push(event.id);
            appointmentsCount++;
          }

          // Hide operatory if it is hidden (events above are still tracked, then discarded)
          if (operatory.hidden) return;

          // Operatory working hours, breaks & exceptions
          const operatorySchedule = providerSchedules.find((s) => s.id === operatoryId && s.locationId === locationId);
          const { events: scheduleEvents, ids: scheduleIds } = buildWorkingHoursAndBreaksEvents(
            operatorySchedule,
            operatoryId,
            operatoryName
          );
          events.push(...scheduleEvents);
          eventIds.push(...scheduleIds);

          // Operatory schedule requests
          // ref: getMappedScheduleRequestData in use-get-calendar-events-v3.ts
          const operatoryRequests = scheduledRequestsWithConvertedTimes.filter(
            (sr) => sr.locationId === locationId && sr.operatoryId === operatoryId
          );
          for (const sr of operatoryRequests) {
            if (!sr.request.id || !sr.startTime) continue;
            const timing = getGridEventTiming(calendarDate, sr.startDate, sr.startTime, sr.endDate, sr.endTime);
            events.push(
              buildScheduleRequestGridEvent(sr, timing, getScopeName, calendarDate, {
                entityId: operatoryId,
                entityName: operatoryName,
              })
            );
            eventIds.push(sr.request.id);
          }

          columns.push({
            id: operatoryId,
            name: operatoryName,
            operatoryIcon: (operatory.details?.icon?.name as IconName) || DEFAULT_OPERATORY_ICON,
            operatoryIconColor: operatory.details?.icon?.color,
            eventIds,
            count: appointmentsCount,
            groupId: locationId,
          } as OperatoryCalendarColumn);
          columnIds.push(operatoryId);
        });

        // Unassigned operatory events
        const unassignedEvents = calendarEventsWithConvertedTimes.filter(
          (e) => e.locationId === locationId && !e.operatoryId
        );
        if (unassignedEvents.length > 0) {
          const unassignedColumnId = `unassigned-operatory-${locationId}`;
          const eventIds: string[] = [];
          for (const event of unassignedEvents) {
            if (!event.id || !event.startTime) continue;
            const timing = getGridEventTiming(
              calendarDate,
              event.startDate,
              event.startTime,
              event.endDate,
              event.endTime
            );
            events.push(buildAppointmentGridEvent(event, timing, getScopeName));
            eventIds.push(event.id);
          }
          columns.push({
            id: unassignedColumnId,
            name: 'Unassigned',
            operatoryIcon: DEFAULT_OPERATORY_ICON as IconName,
            eventIds,
            count: eventIds.length,
            groupId: locationId,
          } as OperatoryCalendarColumn);
          columnIds.push(unassignedColumnId);
        }

        const hasEventsInGroup = columnIds.some(
          (colId) => columns.find((col) => col.id === colId)?.eventIds.length ?? 0 > 0
        );
        const emptyStateVariant = columnIds.length === 0 ? 'no-columns' : !hasEventsInGroup ? 'no-events' : undefined;
        columnGroups.push(
          buildColumnGroup({
            ...columnGroupBaseArgs,
            locationId,
            columnIds,
            groupOffset,
            orderKey: 'operatory',
            emptyStateVariant,
          })
        );
        groupOffset += columnIds.length;
      }

      return { headerType: 'operatory', columns, events, columnGroups, calendarDate };
    } else {
      const columns: ProviderCalendarColumn[] = [];

      for (const locationId of calendarLocationIds) {
        const columnIds: string[] = [];
        const locationProviders = sortedProviders.filter((p) => p.locationId === locationId);

        locationProviders.forEach((provider) => {
          const providerId = provider.id ?? '';
          if (!providerId) return;

          const providerName = getFullName(provider);
          const eventIds: string[] = [];
          let appointmentsCount = 0;

          // Provider appointments
          // ref: getMappedCalendarEventData in use-get-calendar-events-v3.ts
          const providerEvents = calendarEventsWithConvertedTimes.filter(
            (e) => e.locationId === locationId && e.organizerId === providerId
          );
          for (const event of providerEvents) {
            if (!event.id || !event.startTime) continue;
            const timing = getGridEventTiming(
              calendarDate,
              event.startDate,
              event.startTime,
              event.endDate,
              event.endTime
            );
            events.push(
              buildAppointmentGridEvent(event, timing, getScopeName, {
                entityId: providerId,
                entityName: providerName,
                calendarId: provider.calendarId,
              })
            );
            eventIds.push(event.id);
            appointmentsCount++;
          }

          // If provider is archived, show them only if they have events; otherwise show them if they are not hidden
          const isProviderVisible = provider.isArchived ? !!eventIds.length : !provider.hidden;
          if (!isProviderVisible) return;

          // Provider working hours, breaks & exceptions
          const providerSchedule = providerSchedules.find((s) => s.id === providerId && s.locationId === locationId);
          const { events: scheduleEvents, ids: scheduleIds } = buildWorkingHoursAndBreaksEvents(
            providerSchedule,
            providerId,
            providerName
          );
          events.push(...scheduleEvents);
          eventIds.push(...scheduleIds);

          // Provider schedule requests
          // ref: getMappedScheduleRequestData in use-get-calendar-events-v3.ts
          const providerRequests = scheduledRequestsWithConvertedTimes.filter(
            (sr) => sr.locationId === locationId && (sr.organizerId === providerId || sr.providerName === providerName)
          );
          for (const sr of providerRequests) {
            if (!sr.request.id || !sr.startTime) continue;
            const timing = getGridEventTiming(calendarDate, sr.startDate, sr.startTime, sr.endDate, sr.endTime);
            events.push(
              buildScheduleRequestGridEvent(sr, timing, getScopeName, calendarDate, {
                entityId: providerId,
                entityName: providerName,
              })
            );
            eventIds.push(sr.request.id);
          }

          columns.push({
            id: providerId,
            name: getFullName(provider),
            providerImage: provider.profile?.picUrl ?? '',
            eventIds,
            type: provider.isArchived ? 'archived' : undefined,
            count: appointmentsCount,
            groupId: locationId,
          } as ProviderCalendarColumn);
          columnIds.push(providerId);
        });

        // Unassigned provider events
        const unassignedEvents = calendarEventsWithConvertedTimes.filter(
          (e) => e.locationId === locationId && !e.organizerId
        );
        if (unassignedEvents.length > 0) {
          const unassignedColumnId = `unassigned-provider-${locationId}`;
          const eventIds: string[] = [];
          for (const event of unassignedEvents) {
            if (!event.id || !event.startTime) continue;
            const timing = getGridEventTiming(
              calendarDate,
              event.startDate,
              event.startTime,
              event.endDate,
              event.endTime
            );
            events.push(buildAppointmentGridEvent(event, timing, getScopeName));
            eventIds.push(event.id);
          }
          columns.push({
            id: unassignedColumnId,
            name: 'Unassigned',
            providerImage: '',
            eventIds,
            type: 'unassigned',
            count: eventIds.length,
            groupId: locationId,
          } as ProviderCalendarColumn);
          columnIds.push(unassignedColumnId);
        }

        const hasEventsInGroup = columnIds.some(
          (colId) => columns.find((col) => col.id === colId)?.eventIds.length ?? 0 > 0
        );
        const emptyStateVariant = columnIds.length === 0 ? 'no-columns' : !hasEventsInGroup ? 'no-events' : undefined;
        columnGroups.push(
          buildColumnGroup({
            ...columnGroupBaseArgs,
            locationId,
            columnIds,
            groupOffset,
            orderKey: 'provider',
            emptyStateVariant,
          })
        );
        groupOffset += columnIds.length;
      }

      return { headerType: 'provider', columns, events, columnGroups, calendarDate };
    }
  }, [
    calendarLocationIds,
    isOperatoryView,
    getScopeName,
    locationTimezoneMap,
    sortedOperatories,
    calendarDate,
    calendarEventsWithConvertedTimes,
    scheduledRequestsWithConvertedTimes,
    providerSchedules,
    sortedProviders,
    columnEntityOrder,
  ]);
};
